import React, { useState, useEffect } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import "./FinalPackingNavTool.css";
import * as Utils from "../../Utils";

import isu90Alpha from "../../assets/quad1.png";
import isu90Bravo from "../../assets/quad2.png";
import isu90Charlie from "../../assets/quad3.png";
import isu90Delta from "../../assets/quad4.png";
import flat463LImg from "../../assets/463LJ.jpeg";

const FinalPackingNavTool = ({
  selectedPallet,
  quadType,
  storagetype,
  onSelect,
  weightVar = [],
  isu90tabCount = 0,
  flat463LtabCount = 0,
  c463LtabCount = 0,
  nfevtabCount = 0,
  pallet80x120tabCount = 0,
  pallet100x120tabCount = 0,
  onRefreshQuadrant,
  refreshing = false,
}) => {
  const maxVisible = 5; 
  const [startIndex, setStartIndex] = useState(0);
  const [tabButtons, setTabButtons] = useState([]);
  const [percent, setPercent] = useState(""); // For Space Utilization
  const [weight, setWeight] = useState("0");

  const imageMap = {
    "ISU-90": {
      1: isu90Alpha,
      2: isu90Bravo,
      3: isu90Charlie,
      4: isu90Delta,
    },
    "463L": flat463LImg,
    "C463L": flat463LImg,
    "NFEV": flat463LImg,
    "80x120": flat463LImg,
    "100x120": flat463LImg,
  };
  // Generate tab buttons
  useEffect(() => {
    const buttons = [];
    let globalIndex = 0;

    const addButtonsForType = (type, count) => {
      for (let i = 1; i <= count; i++) {
        buttons.push({
          type,
          label: `${type} ${i}`,
          palletID: i,
          weight: weightVar[globalIndex] || 0,
        });
        globalIndex++;
      }
    };

    addButtonsForType("ISU-90", isu90tabCount);
    addButtonsForType("463L", flat463LtabCount);
    addButtonsForType("C463L", c463LtabCount);
    addButtonsForType("NFEV", nfevtabCount);
    addButtonsForType("80x120", pallet80x120tabCount);
    addButtonsForType("100x120", pallet100x120tabCount);

    setTabButtons(buttons);
  }, [
    isu90tabCount,
    flat463LtabCount,
    c463LtabCount,
    nfevtabCount,
    pallet80x120tabCount,
    pallet100x120tabCount,
    weightVar,
  ]);

  const visibleTabs = tabButtons.slice(startIndex, startIndex + maxVisible);

  const handleUp = () => setStartIndex((prev) => Math.max(prev - 1, 0));
  const handleDown = () =>
    setStartIndex((prev) => Math.min(prev + 1, tabButtons.length - maxVisible));

  const handleClick = (palletID, type) => {
    if (type !== "ISU-90") {
      quadType = 1; 
    }
    if (onSelect) onSelect({ palletID, storagetype: type, quad: quadType });
    console.log("PID:"+palletID+", "+type+", "+quadType);
  };

  const selectedTab = tabButtons.find(
    (tab) => tab.palletID === selectedPallet && tab.type === storagetype
  );

  const selectedImage =
    selectedTab?.type === "ISU-90"
      ? imageMap["ISU-90"][quadType] // use quad-specific image
      : imageMap[selectedTab?.type];

  // Fetch space utilization whenever selectedTab changes
  useEffect(() => {
    if (!selectedTab) {
      setPercent("");
      return;
    }

    async function fetchUsage() {
      const currentRunId = sessionStorage.getItem("currentRunId") || "";
      const usage = await Utils.getPercentUsage(
        selectedTab.type,
        selectedTab.palletID,
        currentRunId
      );
      if (usage){
        setPercent((Number(usage.percent).toFixed(2)));
        setWeight((Number(usage.weightPlaced).toFixed(2)));
      }
      else setPercent("N/A");
    }

    fetchUsage();
  }, [selectedTab]);

  const showABCD = selectedTab?.type === "ISU-90";
  //const showABCD = true;

  const abcLabels = ["Alpha", "Bravo", "Charlie", "Delta"];
  const selectedWeight = selectedTab?.weight;

  console.log ("weight:"+selectedWeight)

  return (
    <div className="boxborder">
      {/* Horizontal ABCD buttons */}
      {showABCD && (
        <div className="abcd-buttons">
          {abcLabels.map((label, index) => (
            <button
              key={label}
              className={`abcd-button ${
                quadType === index + 1? "selected" : ""
              }`}
              onClick={() =>
                onSelect({
                  palletID: selectedPallet,
                  storagetype,
                  quad: index + 1,
                })
              }
            >
              {label}
            </button>
          ))}
          {onRefreshQuadrant && (
            <button
              className="refresh-quadrant-button"
              title="Refresh this quadrant (re-run placement after editing box dimensions)"
              onClick={() => onRefreshQuadrant()}
              disabled={refreshing}
            >
              {refreshing ? (
                <CircularProgress size={14} />
              ) : (
                <RefreshIcon style={{ fontSize: 16 }} />
              )}
            </button>
          )}
        </div>
      )}

      {/* Vertical tabs */}
      <div className="vertical-tab-and-image">
        <div className="vertical-tab-container">
          <button
            className="arrow-button"
            onClick={handleUp}
            disabled={startIndex === 0}
          >
            <ArrowUpwardIcon />
          </button>

          {/* Vertical carousel */}
          <div className="tabs-vertical">
            {visibleTabs.map((tab) => (
              <button
                key={`${tab.type}-${tab.palletID}`}
                className={`tab-button ${
                  selectedPallet === tab.palletID && storagetype === tab.type
                    ? "selected"
                    : ""
                }`}
                onClick={() => handleClick(tab.palletID, tab.type)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <button
            className="arrow-button"
            onClick={handleDown}
            disabled={startIndex + maxVisible >= tabButtons.length}
          >
            <ArrowDownwardIcon />
          </button>
        </div>
        {selectedImage && (
          <div className="tab-image-container">
            <img src={selectedImage} alt="Selected type preview" />
          </div>
        )}
      </div>

      {/* Selected info */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {selectedWeight !== undefined && (
          <div className="weight-box">Total Weight: {weight} lbs</div>
        )}
        {percent && (
          <div className="weight-box">Space Utilization: {percent}%</div>
        )}
      </div>
    </div>
  );
};

export default FinalPackingNavTool;
